import { useEffect, useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ExpectationSettingDemoAgentic.module.css';

type Phase = 'running' | 'card' | 'brief' | 'rerunning' | 'brief2';

interface Step {
  time: string;
  text: string;
}

const RUN_1: Step[] = [
  { time: '14:12', text: 'Searched for EU home battery market sources' },
  { time: '14:16', text: 'Read 38 sources, 6 with charts or scanned PDFs' },
  { time: '14:20', text: 'Research complete - brief ready' },
];

const RUN_2: Step[] = [
  { time: '09:01', text: 'Updated sources since last brief' },
  { time: '09:05', text: 'Brief ready - opened directly' },
];

const STEP_MS = 900;

export function ExpectationSettingDemoAgentic() {
  const [phase, setPhase] = useState<Phase>('running');
  const [done, setDone] = useState(0);
  const [checked, setChecked] = useState(false);
  const [feedback, setFeedback] = useState('The research agent is working. Watch what appears before the first brief.');
  const timers = useRef<number[]>([]);

  const later = (fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  };

  const run = (steps: Step[], onDone: () => void) => {
    setDone(0);
    steps.forEach((_, i) => later(() => setDone(i + 1), STEP_MS * (i + 1)));
    later(onDone, STEP_MS * steps.length + 300);
  };

  useEffect(() => {
    const list = timers.current;
    run(RUN_1, () => {
      setPhase('card');
      setFeedback('First run only: a short card says what the agent is strong at, where it slips and one thing to check.');
    });
    return () => list.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showBrief = () => {
    setPhase('brief');
    setFeedback('The card pointed at the market size on page 2. Open its source to check it before sharing.');
  };

  const openSource = () => {
    setChecked(true);
    setFeedback('Checked: the chart reads 4.1 (USD bn), so $4.1B holds. Now run it again to see what a later run looks like.');
  };

  const rerun = () => {
    setPhase('rerunning');
    setChecked(false);
    setFeedback('Three days later, a new run starts...');
    run(RUN_2, () => {
      setPhase('brief2');
      setFeedback('No card this time. You already know what to check, so the brief opens straight away.');
    });
  };

  const isFirst = phase === 'running' || phase === 'card' || phase === 'brief';
  const steps = isFirst ? RUN_1 : RUN_2;
  const running = phase === 'running' || phase === 'rerunning';

  return (
    <div className={kit.root}>
      <div className={kit.message}>Write a market brief on home batteries in the EU.</div>

      <div className={kit.log} aria-label="Agent activity">
        {!isFirst && <div className={kit.cue}>3 days later · run 2</div>}
        {steps.map((s, i) => {
          const state = i < done ? kit.logDotDone : i === done && running ? kit.logDotActive : '';
          if (i > done) return null;
          return (
            <div className={kit.logRow} key={`${phase}-${s.time}`}>
              <span className={kit.logTime}>{s.time}</span>
              <span className={`${kit.logDot} ${state}`} aria-hidden="true" />
              <span className={i < done ? undefined : styles.pending}>{s.text}</span>
              <span />
            </div>
          );
        })}
      </div>

      {phase === 'card' && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>First brief · how to get the most from it</span>
            <span>Shown once</span>
          </div>
          <dl className={`${kit.fields} ${styles.fields}`}>
            <dt>Strong</dt>
            <dd>Summarizing many sources fast</dd>
            <dt>Weaker</dt>
            <dd>Numbers read from charts or scanned PDFs</dd>
            <dt>Check</dt>
            <dd>The market size on page 2 against its source, before sharing</dd>
          </dl>
          <div className={`${kit.row} ${styles.actions}`}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={showBrief}>
              Show brief
            </button>
          </div>
        </div>
      )}

      {(phase === 'brief' || phase === 'brief2') && (
        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Brief · Home battery market, EU</span>
            <span>{phase === 'brief' ? 'Run 1' : 'Run 2'}</span>
          </div>
          <h3 className={kit.proposalTitle}>
            {phase === 'brief' ? 'Growth driven by solar self-use' : 'Update: prices down 8% since last brief'}
          </h3>
          <dl className={kit.fields}>
            <dt>Page 1</dt>
            <dd>Summary of 38 sources: demand is led by Germany and Italy.</dd>
            <dt>Page 2</dt>
            <dd>
              <span className={styles.figure}>
                Market size $4.1B <span className={kit.label}>(source 12, chart)</span>
              </span>
              {phase === 'brief' && (
                <span className={`${kit.chip} ${checked ? kit.chipGood : kit.chipWarn} ${styles.flag}`}>
                  {checked ? 'Checked' : 'Check this'}
                </span>
              )}
            </dd>
          </dl>
          {phase === 'brief' && checked && (
            <div className={styles.source}>
              <span className={kit.label}>Source 12 · EU Storage Outlook, fig. 3</span>
              <p>Chart value for 2025: 4.1 (USD bn). Matches the brief.</p>
            </div>
          )}
          <div className={`${kit.row} ${styles.actions}`}>
            {phase === 'brief' && !checked && (
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={openSource}>
                Open source 12
              </button>
            )}
            {phase === 'brief' && checked && (
              <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={rerun}>
                Run again (3 days later)
              </button>
            )}
            {phase === 'brief2' && <span className={kit.chip}>No intro card · opened directly</span>}
          </div>
        </div>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
